import _ from 'underscore';
import { UrlBuilders, UrlHelper } from '@bingads-webui-clientcenter/url-builder';
import globalApps from '@bingads-webui-clientcenter/global-apps';
import CouponSteps from './coupon-steps';
import { LoginAssociateStepKeys, LoginAssociateStepKeysTaxable, SignupSuccessStepKeys, SignupSuccessStepKeysTaxable, ServerI18nKeyMap } from '../constants';

const { BillingUrlBuilder } = UrlBuilders;
const CouponOfferDetailsLinkId = 2023933;
const CouponOfferRetentionDetailsLinkId = 2128648;
const ContactSupportLinkId = 398371;

const getI18n = () => globalApps.getI18nFromMap('coupon-lightbox');

function getEscapedString(key, model) {
  return getI18n().getString(key, model, {
    preprocess: _.escape,
  });
}

const LinkStop = '</a>';
const OfferDetailsModel = {
  offerLinkStart: `<a href="${UrlHelper.getForwardLink(CouponOfferDetailsLinkId)}" target="_blank" >`,
  offerLinkStop: LinkStop,
};
const OfferDetailsModelRetention = {
  offerLinkStart: `<a href="${UrlHelper.getForwardLink(CouponOfferRetentionDetailsLinkId)}" target="_blank" >`,
  offerLinkStop: LinkStop,
};

const getTimeIsTicking = () => getEscapedString(_TL_('Time is ticking. Act now! {{offerLinkStart}}Offer details{{offerLinkStop}}'), OfferDetailsModel);
const getTimeIsTickingRetention = () => getEscapedString(_TL_('Time is ticking. Act now! {{offerLinkStart}}Offer details{{offerLinkStop}}'), OfferDetailsModelRetention);
const SupportLinkModel = {
  supportLinkStart: `<a href="${UrlHelper.getForwardLink(ContactSupportLinkId)}" target="_blank" >`,
  supportLinkStop: LinkStop,
};

const required = (paramName) => { throw Error(`Parameter '${paramName}' is Required`); };

function renderLightbox({
  LightboxService,
  title,
  buttonKey,
  buttonAction = null,
  showCloseButton,
  additionalMessageClass,
  mainMessage,
  midMessageBlock,
  subMessage,
  cancelKey,

}) {
  const lightbox = new LightboxService({
    el: '.lightboxDiv',
    title,
    messageBlock: mainMessage,
    midMessageBlock,
    subMessageBlock: subMessage,
    primaryButton: {
      text: getI18n().getString(buttonKey),
      event: buttonAction,
    },
    defaultButton: cancelKey
      ? {
        text: getI18n().getString(cancelKey),
        event: null,
      }
      : undefined,
    additionalMessageClass,
    imageClass: 'coupon-svg',
    showCloseButton,
  });

  lightbox.render();
  return lightbox;
}

function GetLoginAssociationMidContent(isAccountTaxable, upfrontSpend, expireDate) {
  const stepKeys = isAccountTaxable ? LoginAssociateStepKeysTaxable : LoginAssociateStepKeys;
  return new CouponSteps({
    steps: _.map(stepKeys, key => getEscapedString(key, {
      upfrontSpend: _.escape(upfrontSpend),
      expireDate: _.escape(expireDate),
    })),
  });
}

/**
  @summary Renders coupon success lightbox from the signup flow.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @param {string} upfrontSpend - The amount (with currency) that needs to be spent to use the coupon.
  @param {string} expireDate - The date the coupon will expire.
  @param {string} value - The amount (with currency) of additional advertising added to the account after upfront is spent.
  @param {boolean} isAccountTaxable - Is the current account subject to taxation based on its business address.
  @param {function|string} buttonAction - event or url to redirect when clicking. Will trigger hide event for the lightbox when this parameter is null.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function signupSuccess({
  LightboxService = required('LightboxService'),
  upfrontSpend = required('upfrontSpend'),
  expireDate = required('expireDate'),
  value = required('value'),
  isAccountTaxable = required('isAccountTaxable'),
  buttonAction = null,
}) {
  const title = getEscapedString(_TL_('Special offer: Turn {{upfrontSpend}} into {{value}} worth of advertising credits on Microsoft Advertising'), {
    upfrontSpend: _.escape(upfrontSpend),
    value: _.escape(value),
  });

  const buttonKey = _TL_('Got it!');
  const mainMessage = getEscapedString(_TL_("To unlock this limited-time offer, you'll need to:"));
  const stepKeys = isAccountTaxable ? SignupSuccessStepKeysTaxable : SignupSuccessStepKeys;
  const midMessageBlock = new CouponSteps({
    steps: _.map(stepKeys, key => getEscapedString(key, {
      upfrontSpend: _.escape(upfrontSpend),
      expireDate: _.escape(expireDate),
    })),
  });

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: false,
    additionalMessageClass: 'coupon-success',
    mainMessage,
    midMessageBlock,
    subMessage: getTimeIsTicking(),
  });
}

/**
  @summary Renders coupon failure lightbox from the signup flow.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @param {function|string} buttonAction - event or url to redirect when clicking. Will trigger hide event for the lightbox when this parameter is null.
  @param {string} titleKey - key of title to show.
  @param {string} mainMessageKey - key of message to show when failure occurs.
  @param {object} mainMessageModel - values to format into mainMessage.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function signupFailure({
  LightboxService = required('LightboxService'),
  buttonAction = null,
  titleKey = _TL_('Welcome to Microsoft Advertising!'),
  mainMessageKey = _TL_("We're sorry, you don't qualify for a new customer offer. {{offerLinkStart}}Offer details{{offerLinkStop}}"),
} = { buttonAction: null }) {
  const title = getI18n().getString(ServerI18nKeyMap[titleKey] || titleKey);
  const buttonKey = _TL_('Continue');

  const mainMessage = getEscapedString(
    ServerI18nKeyMap[mainMessageKey] || mainMessageKey,
    _.extend(
      {},
      OfferDetailsModel,
      SupportLinkModel
    )
  );

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: false,
    additionalMessageClass: 'coupon-failure',
    mainMessage,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when a coupon has been associated.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @param {string} upfrontSpend - The amount (with currency) that needs to be spent to use the coupon.
  @param {string} value - The amount (with currency) of additional advertising added to the account after upfront is spent.
  @param {string} expireDate - The date the coupon will expire.
  @param {boolean} isAccountTaxable - Is the current account subject to taxation based on its business address.
  @param {function|string} buttonAction - event or url to redirect when clicking. Will trigger hide event for the lightbox when this parameter is null.
  @param {boolean} isRetention - Is the coupon a retention coupon.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginAssociated({
  LightboxService = required('LightboxService'),
  upfrontSpend = required('upfrontSpend'),
  value = required('value'),
  expireDate = required('expireDate'),
  isAccountTaxable = required('isAccountTaxable'),
  buttonAction = null,
  isRetention = false,
}) {
  const title = isRetention
    ? getI18n().getString(_TL_('Spend {{upfrontSpend}}, get {{value}}!'), { upfrontSpend, value })
    : getI18n().getString(_TL_('{{value}} credit from Microsoft Advertising!'), { value });
  const buttonKey = _TL_('Add a payment method');

  const mainMessage = isRetention
    ? getEscapedString(
      _TL_('Receive a {{value}} credit when you:'),
      {
        value: _.escape(value),
      }
    )
    : getI18n().getString(_TL_("We've added a one-time advertising credit to your account. To redeem this special offer:"));
  const midMessageBlock = GetLoginAssociationMidContent(isAccountTaxable, upfrontSpend, expireDate);
  const cancelKey = _TL_("I'll do it later");

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-associated',
    mainMessage,
    midMessageBlock,
    subMessage: isRetention
      ? getTimeIsTickingRetention()
      : getTimeIsTicking(),
    cancelKey,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when a coupon has been redeemed.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @param {string} upfrontSpend - The amount (with currency) that needs to be spent to use the coupon.
  @param {string} value - The amount (with currency) of additional advertising added to the account after upfront is spent.
  @param {string} expireDate - The date the coupon will expire.
  @param {boolean} isAccountTaxable - Is the current account subject to taxation based on its business address.
  @param {boolean} isRetention - Is the coupon a retention coupon.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginRedeemed({
  LightboxService = required('LightboxService'),
  upfrontSpend = required('upfrontSpend'),
  value = required('value'),
  expireDate = required('expireDate'),
  isAccountTaxable = required('isAccountTaxable'),
  isRetention = false,
}) {
  const title = isRetention
    ? getI18n().getString(_TL_('Spend {{upfrontSpend}}, get {{value}}!'), { upfrontSpend, value })
    : getI18n().getString(_TL_('{{value}} credit from Microsoft Advertising!'), { value });
  const buttonKey = _TL_('View Promotions');
  // eslint-disable-next-line no-nested-ternary
  const messageKey = isRetention
    ? (
      isAccountTaxable
        ? _TL_('Receive a {{value}} credit valid through {{expireDate}} when you spend {{upfrontSpend}} (excluding taxes) by that date.')
        : _TL_('Receive a {{value}} credit valid through {{expireDate}} when you spend {{upfrontSpend}} by that date.')
    ) : (
      isAccountTaxable
        ? _TL_('Welcome to Microsoft Advertising! Get {{value}} in advertising credits when you spend {{upfrontSpend}} (excluding taxes) by {{expireDate}}.')
        : _TL_('Welcome to Microsoft Advertising! Get {{value}} in advertising credits when you spend {{upfrontSpend}} by {{expireDate}}.')
    );

  const mainMessage = getEscapedString(messageKey, {
    upfrontSpend: _.escape(upfrontSpend),
    value: _.escape(value),
    expireDate: _.escape(expireDate),
  });
  const cancelKey = _TL_('Dismiss');
  const buttonAction = new BillingUrlBuilder().action('CouponDetails').url;

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-redeemed',
    mainMessage,
    midMessageBlock: isRetention
      ? getTimeIsTickingRetention()
      : getTimeIsTicking(),
    cancelKey,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when a coupon has been associated already.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @param {string} upfrontSpend - The amount (with currency) that needs to be spent to use the coupon.
  @param {string} value - The amount (with currency) of additional advertising added to the account after upfront is spent.
  @param {string} expireDate - The date the coupon will expire.
  @param {boolean} isAccountTaxable - Is the current account subject to taxation based on its business address.
  @param {function|string} buttonAction - event or url to redirect when clicking. Will trigger hide event for the lightbox when this parameter is null.
  @param {boolean} isRetention - Is the coupon a retention coupon.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginPreviouslyAssociated({
  LightboxService = required('LightboxService'),
  upfrontSpend = required('upfrontSpend'),
  value = required('value'),
  expireDate = required('expireDate'),
  isAccountTaxable = required('isAccountTaxable'),
  buttonAction = null,
  isRetention = false,
}) {
  const title = isRetention
    ? getI18n().getString(_TL_('Spend {{upfrontSpend}}, get {{value}}!'), { upfrontSpend, value })
    : getI18n().getString(_TL_('{{value}} credit from Microsoft Advertising!'), { value });

  const buttonKey = _TL_('Add a payment method');

  const mainMessage = getEscapedString(
    isRetention
      ? _TL_("To unlock the {{value}} promotional offer that we've added to your account, you'll need to:")
      : _TL_("Welcome to Microsoft Advertising! To unlock the {{value}} promotional offer that we've added to your account, you'll need to:"),
    { value: _.escape(value) }
  );
  const midMessageBlock = GetLoginAssociationMidContent(isAccountTaxable, upfrontSpend, expireDate);
  const cancelKey = _TL_("I'll do it later");

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-prev-associated',
    mainMessage,
    midMessageBlock,
    subMessage: isRetention
      ? getTimeIsTickingRetention()
      : getTimeIsTicking(),
    cancelKey,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when a pbc has already been redeemed for the given account.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginPreviouslyRedeemed({
  LightboxService = required('LightboxService'),
}) {
  const title = getI18n().getString(_TL_('This special offer has already been redeemed'));
  const buttonKey = _TL_('View Promotions');
  const mainMessage = getEscapedString(_TL_('To see the status of this offer, go to the Promotions page for more information.'));
  const cancelKey = _TL_('Dismiss');
  const buttonAction = new BillingUrlBuilder().action('CouponDetails').url;

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    buttonAction,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-prev-redeemed',
    mainMessage,
    cancelKey,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when the coupon has already been redeemed, or is expired.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginNotAvailable({
  LightboxService = required('LightboxService'),
}) {
  const title = getI18n().getString(_TL_('This offer is no longer available'));
  const buttonKey = _TL_('Aw, shucks');
  const mainMessage = getEscapedString(_TL_("We're sorry, but this offer is no longer available because it's already been redeemed or it expired."));

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-not-available',
    mainMessage,
  });
}

/**
  @summary Renders coupon lightbox from the login flow when there is any unexpected error.
  @param {Backbone.View} LightboxService - The Lightbox Service from clientcenter/feature.
  @return {Backbone.View} Returns Instance of LightboxService.
  */
export function loginGenericError({
  LightboxService = required('LightboxService'),
}) {
  const title = getI18n().getString(_TL_('Contact us to get your promotional code'));
  const buttonKey = _TL_('Got it!');
  const mainMessage = getEscapedString(_TL_('We encountered a snag while printing your promotional code. Sorry about that! Please {{supportLinkStart}}contact support{{supportLinkStop}} to get your promotional code.'), SupportLinkModel);

  return renderLightbox({
    LightboxService,
    title,
    buttonKey,
    showCloseButton: true,
    additionalMessageClass: 'coupon-login-error',
    mainMessage,
  });
}
